import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Lock } from "lucide-react";
import { api, apiError } from "@/lib/fda";
import { getAttribution } from "@/lib/attribution";
import { QUESTIONS, CONTACT_TIMES } from "@/lib/questions";
import {
  CONSENT_FDA_TEXT, CONSENT_MARKETING_TEXT, CONSENT_THIRD_PARTY_TEXT,
  CONSENT_VERSION, MARKETING_CHANNELS,
} from "@/lib/legal";

const DRAFT = "fda_funnel_draft";
const TOTAL = QUESTIONS.length + 1;

const uid = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `k${Date.now()}${Math.random()}`);

function track(event_type, metadata = {}) {
  api.post("/funnel-events", { event_type, metadata }).catch(() => {});
}

const Option = ({ label, selected, onClick, testid, multi }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={selected}
    className={`flex w-full items-center gap-4 rounded-xl border-2 px-5 py-4 text-left text-sm font-semibold transition-all
      ${selected ? "border-terracotta bg-terracotta/10 text-forest" : "border-border bg-white text-forest hover:border-forest-soft"}`}>
    <span className={`grid h-5 w-5 shrink-0 place-items-center border-2 ${multi ? "rounded" : "rounded-full"} ${selected ? "border-terracotta bg-terracotta" : "border-border"}`}>
      {selected && <span className="h-2 w-2 rounded-full bg-white" />}
    </span>
    {label}
  </button>
);

export default function Funnel() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [a, setA] = useState({});
  const [contact, setContact] = useState({ first_name: "", last_name: "", phone: "", email: "", preferred_contact_time: "" });
  const [consents, setConsents] = useState({ fda: false, third: false, marketing: false });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const keyRef = useRef(null);
  const startedRef = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");
  const topRef = useRef(null);

  useEffect(() => {
    const raw = localStorage.getItem(DRAFT);
    if (raw) {
      try {
        const d = JSON.parse(raw);
        setA(d.a || {}); setContact((c) => ({ ...c, ...(d.contact || {}) })); setStep(d.step || 0);
      } catch { /* ignore */ }
    }
    track("ASSESSMENT_STARTED");
  }, []);

  useEffect(() => {
    localStorage.setItem(DRAFT, JSON.stringify({ a, contact, step }));
  }, [a, contact, step]);

  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
    const beforeUnload = (e) => { if (step > 0 && step < TOTAL) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [step]);

  const q = QUESTIONS[step];
  const isContactStep = step === QUESTIONS.length;

  const questionValid = useMemo(() => {
    if (!q) return true;
    const v = a[q.key];
    if (q.type === "multi") {
      if (!v || v.length === 0) return false;
      if (q.otherOption && v.includes(q.otherOption) && !(a[q.otherKey] || "").trim()) return false;
      return true;
    }
    if (!v) return false;
    if (q.followUp && q.followUp.showFor.includes(v) && !a[q.followUp.key]) return false;
    const tf = q.textFollowUp?.[v];
    if (tf?.required && !(a[tf.key] || "").trim()) return false;
    return true;
  }, [q, a]);

  const emailOk = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(contact.email.trim());
  const phoneOk = /^(?:\+44|0044|0)7\d{9}$|^(?:\+44|0044|0)[1238]\d{8,9}$/.test(contact.phone.replace(/[\s()-]/g, ""));
  const contactValid = contact.first_name.trim().length > 1 && contact.last_name.trim().length > 1 &&
    phoneOk && emailOk && contact.preferred_contact_time && consents.fda;

  const next = () => {
    setError("");
    if (!questionValid) { setError("Please choose an answer to continue."); return; }
    track(`QUESTION_${step + 1}_COMPLETED`);
    setStep(step + 1);
  };

  const submit = async () => {
    setError("");
    if (!contactValid) { setError("Please complete all fields and agree to be contacted about your enquiry."); return; }
    if (submitting) return;
    setSubmitting(true);
    if (!keyRef.current) keyRef.current = uid();

    const payload = {
      idempotency_key: keyRef.current,
      ...contact,
      phone: contact.phone.replace(/[\s()-]/g, ""),
      situation: a.situation, debt_range: a.debt_range, creditor_count: a.creditor_count,
      debt_types: a.debt_types || [], debt_types_other: a.debt_types_other || null,
      payment_status: a.payment_status, arrears_duration: a.arrears_duration || null,
      household_income: a.household_income, disposable_income: a.disposable_income,
      accommodation_type: a.accommodation_type, existing_debt_help: a.existing_debt_help,
      existing_solution_type: a.existing_solution_type || null, primary_goal: a.primary_goal,
      attribution: getAttribution(),
      company_website: honeypot,
      form_duration_ms: Date.now() - startedRef.current,
      consents: [
        { consent_type: "FDA_CONTACT", consent_text: CONSENT_FDA_TEXT, consent_version: CONSENT_VERSION },
        ...(consents.third ? [{ consent_type: "THIRD_PARTY", consent_text: CONSENT_THIRD_PARTY_TEXT, consent_version: CONSENT_VERSION }] : []),
        ...(consents.marketing ? [{ consent_type: "MARKETING", consent_text: CONSENT_MARKETING_TEXT, consent_version: CONSENT_VERSION, channels: MARKETING_CHANNELS }] : []),
      ],
    };

    try {
      const { data } = await api.post("/leads", payload);
      localStorage.removeItem(DRAFT);
      sessionStorage.setItem("fda_submitted_lead", JSON.stringify({ ...payload, lead_reference: data.lead_reference }));
      nav("/assessment/complete", { replace: true });
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || "Something went wrong while submitting your assessment. Please try again.");
      setSubmitting(false);
    }
  };

  const pct = Math.round(((step + (isContactStep ? 1 : 0)) / TOTAL) * 100);

  return (
    <div className="min-h-[80vh] bg-sand px-5 py-10 lg:px-10" data-testid="funnel-page">
      <div ref={topRef} className="mx-auto max-w-2xl">
        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-[0.15em] text-forest-soft">
            <span data-testid="funnel-progress-label">
              {isContactStep ? `Question ${TOTAL} of ${TOTAL}` : `Question ${step + 1} of ${TOTAL}`}
            </span>
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3 w-3" /> Secure</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-sand-deep" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-terracotta transition-all duration-500" style={{ width: `${Math.max(pct, 6)}%` }} />
          </div>
        </div>

        {!isContactStep ? (
          <div key={q.key} className="rise">
            <h1 className="font-display text-2xl font-extrabold leading-tight text-forest sm:text-3xl">{q.title}</h1>
            {q.help && <p className="mt-2 text-sm text-forest-soft">{q.help}</p>}
            <div className="mt-7 space-y-3">
              {q.options.map((o, i) => {
                const sel = q.type === "multi" ? (a[q.key] || []).includes(o) : a[q.key] === o;
                return (
                  <Option key={o} label={o} selected={sel} multi={q.type === "multi"} testid={`option-${step}-${i}`}
                    onClick={() => {
                      setError("");
                      if (q.type === "multi") {
                        const cur = a[q.key] || [];
                        setA({ ...a, [q.key]: sel ? cur.filter((x) => x !== o) : [...cur, o] });
                      } else {
                        setA({ ...a, [q.key]: o });
                      }
                    }} />
                );
              })}
            </div>

            {q.otherOption && (a[q.key] || []).includes(q.otherOption) && (
              <div className="mt-5">
                <label className="text-sm font-semibold text-forest" htmlFor="other-desc">{q.otherLabel}</label>
                <input id="other-desc" data-testid="other-debt-description" value={a[q.otherKey] || ""}
                  onChange={(e) => setA({ ...a, [q.otherKey]: e.target.value })}
                  className="mt-2 w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-sm" />
              </div>
            )}

            {q.followUp && q.followUp.showFor.includes(a[q.key]) && (
              <div className="mt-8 rounded-xl border-2 border-border bg-white/60 p-5" data-testid="followup-block">
                <p className="font-display text-lg font-bold text-forest">{q.followUp.title}</p>
                <div className="mt-4 space-y-3">
                  {q.followUp.options.map((o, i) => (
                    <Option key={o} label={o} selected={a[q.followUp.key] === o} testid={`followup-option-${i}`}
                      onClick={() => { setError(""); setA({ ...a, [q.followUp.key]: o }); }} />
                  ))}
                </div>
              </div>
            )}

            {q.textFollowUp?.[a[q.key]] && (
              <div className="mt-8" data-testid="text-followup-block">
                <label className="text-sm font-semibold text-forest" htmlFor="tf">{q.textFollowUp[a[q.key]].title}</label>
                <input id="tf" data-testid="existing-solution-type" value={a[q.textFollowUp[a[q.key]].key] || ""}
                  onChange={(e) => setA({ ...a, [q.textFollowUp[a[q.key]].key]: e.target.value })}
                  placeholder="For example: Debt Management Plan"
                  className="mt-2 w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-sm" />
              </div>
            )}
          </div>
        ) : (
          <div className="rise" data-testid="contact-step">
            <h1 className="font-display text-2xl font-extrabold text-forest sm:text-3xl">You're nearly there.</h1>
            <p className="mt-2 text-sm text-forest-soft">Tell us how to reach you and we'll take it from here.</p>
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {[["first_name", "First name", "text"], ["last_name", "Last name", "text"],
                ["phone", "Mobile number", "tel"], ["email", "Email address", "email"]].map(([k, label, type]) => (
                <div key={k} className={k === "phone" || k === "email" ? "sm:col-span-2" : ""}>
                  <label htmlFor={k} className="text-sm font-semibold text-forest">{label}</label>
                  <input id={k} type={type} data-testid={`input-${k.replace("_", "-")}`} value={contact[k]}
                    autoComplete={k === "phone" ? "tel" : k === "email" ? "email" : k === "first_name" ? "given-name" : "family-name"}
                    onChange={(e) => { setError(""); setContact({ ...contact, [k]: e.target.value }); }}
                    className="mt-2 w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-sm" />
                  {k === "phone" && contact.phone && !phoneOk && <p className="mt-1 text-xs font-semibold text-destructive" data-testid="phone-error">Please enter a valid UK telephone number.</p>}
                  {k === "email" && contact.email && !emailOk && <p className="mt-1 text-xs font-semibold text-destructive" data-testid="email-error">Please enter a valid email address.</p>}
                </div>
              ))}
            </div>

            <p className="mt-7 text-sm font-semibold text-forest">Preferred contact time</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {CONTACT_TIMES.map((t, i) => (
                <Option key={t} label={t} selected={contact.preferred_contact_time === t} testid={`contact-time-${i}`}
                  onClick={() => { setError(""); setContact({ ...contact, preferred_contact_time: t }); }} />
              ))}
            </div>

            <div className="mt-8 space-y-4">
              <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden">
                <label htmlFor="company_website">Company website</label>
                <input id="company_website" name="company_website" type="text" tabIndex={-1}
                  autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
              </div>
              {[["fda", CONSENT_FDA_TEXT, "consent-fda", true],
                ["third", CONSENT_THIRD_PARTY_TEXT, "consent-third-party", false],
                ["marketing", CONSENT_MARKETING_TEXT, "consent-marketing", false]].map(([k, text, testid, required]) => (
                <label key={k} className="flex cursor-pointer gap-4 rounded-xl border-2 border-border bg-white p-4">
                  <input type="checkbox" data-testid={testid} checked={consents[k]}
                    onChange={(e) => { setError(""); setConsents({ ...consents, [k]: e.target.checked }); }}
                    className="mt-1 h-5 w-5 shrink-0 accent-terracotta" />
                  <span className="text-xs leading-relaxed text-forest-soft">
                    {required && <span className="mr-1 font-bold uppercase tracking-wider text-terracotta">Required</span>}
                    {text}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {error && <p role="alert" data-testid="funnel-error" className="mt-6 rounded-xl border-2 border-destructive bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">{error}</p>}

        <div className="mt-8 flex items-center gap-4">
          {step > 0 && (
            <button type="button" data-testid="funnel-back" onClick={() => { setError(""); setStep(step - 1); }}
              className="inline-flex items-center gap-2 rounded-full border-2 border-border bg-white px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-forest">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
          {!isContactStep ? (
            <button type="button" data-testid="funnel-continue" onClick={next}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-white transition-transform hover:-translate-y-0.5">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" data-testid="funnel-submit" onClick={submit} disabled={submitting}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-white disabled:opacity-60">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Submit my assessment</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
